import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { LinearProgress } from '@mui/material';
import { PublishedWithChanges, Unpublished, ChangeCircle, Warning } from '@mui/icons-material';
import StagingDataTable from './StagingDataTable';
import { selectStagingPoints } from '../../../redux/selectors/staging';
import BuildingStagingLoader from '../../loaders/BuildingStagingLoader';
import { Page, Row } from '../../util/Page';
import ColumnDefinition from '../constants/ColumnDefinition';
import TableAction from '../constants/TableAction';
import { updateStaging } from '../../../redux/actions/staging';
import { dateTime } from '../../../utils/parsing';
import StatusBadge from '../constants/StatusBadge';
import { SET_SELECTED_POINTS } from '../../../redux/actions/actiontypes';

const PointsDataTable = ({
  loaded,
  buildingId,
  filteredPoints,
  units,
  pointTypes,
  updateStagingUncaught,
  pointsValidation,
  selectedPoints,
}) => {
  if (!loaded) {
    return (
      <Page>
        <Row>
          <BuildingStagingLoader key={`stg-${buildingId}`} buildingId={buildingId} />
          <LinearProgress color="secondary" className="loading" />
        </Row>
      </Page>
    );
  }

  if (filteredPoints.length <= 0) {
    return (
      <Page>
        <Row>There are no results to show.</Row>
      </Page>
    );
  }

  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const columns = [
    new ColumnDefinition({
      id: 'status',
      label: 'Status',
      type: 'badges',
      sortable: true,
      render: (point) => {
        const statuses = [];
        if (!point.last_promoted)
          statuses.push(
            new StatusBadge({
              id: 'promoted',
              icon: Unpublished,
              message: 'This point is not promoted.',
            })
          );
        else
          statuses.push(
            new StatusBadge({
              id: 'unpromoted',
              icon: PublishedWithChanges,
              color: 'success',
              message: `This point was last promoted ${dateTime(point.last_promoted)}.`,
            })
          );
        if (pointsValidation?.[point?.topic]?.length > 0)
          statuses.push(
            new StatusBadge({
              id: 'promotion_error',
              icon: Warning,
              color: 'error',
              message: `This point contains validation errors:${[
                '',
                ...pointsValidation[point.topic],
              ].join('\n  -> ')}`,
            })
          );
        if (point.last_promoted && point.last_promoted < point.modified)
          statuses.push(
            new StatusBadge({
              id: 'changes',
              icon: ChangeCircle,
              color: 'warning',
              message: `This point has unpromoted changes.`,
            })
          );
        return statuses;
      },
    }),
    new ColumnDefinition({
      id: 'point_type',
      label: 'Point Type',
      type: 'button',
      sortable: true,
      action: 'change_point_type',
      render: (point) => pointTypes[point.point_type_id]?.name,
    }),
    new ColumnDefinition({
      id: 'confidence',
      label: 'Confidence',
      type: 'linear',
      sortable: true,
      sortFunction: (pointA, pointB) => {
        const valueA = pointA.confidences.point_type || 0;
        const valueB = pointB.confidences.point_type || 0;
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      },
      render: (point) => point.confidences.point_type,
    }),
    new ColumnDefinition({
      id: 'point_name',
      label: 'Point Name',
      type: 'text',
      render: (point) => point.data.name,
    }),
    new ColumnDefinition({
      id: 'point_description',
      label: 'Description',
      type: 'text',
      render: (point) => point.data.description,
    }),
    new ColumnDefinition({
      id: 'point_value',
      label: 'Present Value',
      type: 'text',
      render: (point) => point.data.presentValue,
    }),
    new ColumnDefinition({
      id: 'units',
      label: 'Units',
      type: 'button',
      action: 'change_unit_type',
      render: (point) => units[point.raw_unit_id]?.name_abbr,
    }),
    new ColumnDefinition({
      id: 'equip_id',
      label: 'Equip ID',
      type: 'button',
      action: 'change_equip_id',
      render: (point) => point.equip_id,
    }),
  ];

  const update = (data, newPointValues) => {
    // Only need the unique set, will convert back to list before we make the next call
    const equipIds = new Set(data.map((datum) => datum.equip_id));
    if (newPointValues['p.equip_id']) equipIds.add(newPointValues['p.equip_id']);
    const updateRowValues = data.map((datum) => ({ ...newPointValues, 'p.topic': datum.topic }));
    setLoading(true);
    // Currently skip equipment isn't actually actualized or streamlined, so if we're trying to skip something, start with a SKIP equip to ensure it exists
    if (equipIds.has('__SKIP__')) updateRowValues.unshift({ 'e.equip_id': '__SKIP__' });
    return updateStagingUncaught([...equipIds], updateRowValues).then(() => {
      setLoading(false);
    });
  };

  const actions = [
    new TableAction({
      id: 'change_point_type',
      label: 'Change Point Type',
      doSave: update,
    }),
    new TableAction({
      id: 'change_unit_type',
      label: 'Change Unit Type',
      doSave: update,
    }),
    new TableAction({
      id: 'change_equip_id',
      label: 'Change Equip Id',
      doSave: update,
    }),
  ];

  return (
    <StagingDataTable
      title="Points"
      columns={columns}
      actions={actions}
      data={filteredPoints}
      rowIdKey="topic"
      selected={selectedPoints}
      onItemSelected={(newPoints) => dispatch({ type: SET_SELECTED_POINTS, points: newPoints })}
      loading={loading}
      buildingId={buildingId}
    />
  );
};

const mapStateToProps = (state, { buildingId }) => {
  const {
    entities: {
      staging: {
        validation: { points },
        selectedPoints,
      },
    },
  } = state;
  const pointsValidation = points[buildingId ?? ''] ?? {};
  // Including filtered point as points
  const staging = selectStagingPoints(state, buildingId);
  // We want __SKIP__ points last, so until we replace the backend, we'll just do some rearranging
  const reorderedPoints = staging.filteredPoints
    .filter((row) => row.equip_id !== '__SKIP__')
    .concat(staging.filteredPoints.filter((row) => row.equip_id === '__SKIP__'));
  return {
    loaded: staging.loaded,
    filteredPoints: reorderedPoints,
    buildingId,
    units: state.entities.units.byId,
    pointTypes: state.entities.pointTypes.byId,
    pointsValidation,
    selectedPoints,
  };
};

const mapDispatchToProps = (dispatch, { buildingId }) => ({
  updateStagingUncaught: (equipId, data) => dispatch(updateStaging(buildingId, equipId, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PointsDataTable);
